import React from 'react';
import PropTypes from 'prop-types';
import { Modal } from '@telus-uds/components-web';
import styled, { css, createGlobalStyle } from 'styled-components';

const StyledModalContent = styled.div`
  ${props =>
    props.customStyles &&
    (typeof props.customStyles === 'string'
      ? css`
          ${props.customStyles}
        `
      : typeof props.customStyles === 'object' &&
        Object.keys(props.customStyles).length > 0 &&
        css`
          ${Object.entries(props.customStyles)
            .map(([key, value]) => {
              const cssKey = key.replace(/([A-Z])/g, '-$1').toLowerCase();
              return `${cssKey}: ${value};`;
            })
            .join('\n')}
        `)}
`;

/* Override UDS Modal's inline max-width (576px from theme) when minWidth is set. Portal renders dialog outside our tree so we target the dialog directly. */
const ModalWidthOverride = createGlobalStyle`
  [role="dialog"] [style*="max-width"] {
    max-width: ${props => props.width || '1280px'} !important;
  }
`;

// Maps semantic `size` prop to UDS Modal `variant.width` (native UDS sizes).
const SIZE_VARIANT_MAP = {
  small: 's',   // 320px
  medium: 'm'   // 480px
  // 'large' uses the global width override below (UDS native 'l' is only 630px,
  // narrower than the existing 'xl' default — too small for FM product/licensing tables).
};

// Maps semantic `size` prop to a global max-width override (in px).
// Used when the desired width exceeds UDS's native scale.
const SIZE_OVERRIDE_MAP = {
  large: '1024px'
};

const ModalCustom = ({
  open = false,
  setOpen,
  header,
  children,
  customStyles = null,
  hideCloseButton,
  minWidth,
  size,
  ...otherProps
}) => {
  const variantWidth = SIZE_VARIANT_MAP[size] || 'xl';
  // Explicit `minWidth` always wins over `size` (back-compat with existing 1280px callers).
  const overrideWidth = minWidth || SIZE_OVERRIDE_MAP[size];

  return (
    <>
      {overrideWidth && open && <ModalWidthOverride width={overrideWidth} />}
      <Modal
        isOpen={open}
        onClose={() => setOpen()}
        heading={header}
        closeButton={hideCloseButton ? null : undefined}
        variant={{ width: variantWidth }}
        {...otherProps}
      >
        {customStyles ? (
          <StyledModalContent customStyles={customStyles}>
            {children}
          </StyledModalContent>
        ) : (
          children
        )}
      </Modal>
    </>
  );
};

ModalCustom.propTypes = {
  open: PropTypes.bool,
  setOpen: PropTypes.func.isRequired,
  header: PropTypes.string,
  children: PropTypes.node.isRequired,
  customStyles: PropTypes.oneOfType([
    PropTypes.object,
    PropTypes.string
  ]),
  hideCloseButton: PropTypes.bool,
  /** Min width for the modal dialog (passed to UDS Modal as maxWidth to set dialog width). Use e.g. "1280px" for Add Offer. */
  minWidth: PropTypes.string,
  /** Semantic size preset. 'small' = 320px, 'medium' = 480px, 'large' = 1024px (for product/licensing tables). Omit for the default 768px (UDS xl). */
  size: PropTypes.oneOf(['small', 'medium', 'large'])
};

export default ModalCustom;
